import { Component, Inject, OnInit, OnDestroy } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef, MatDialog } from '@angular/material/dialog';
import { FormArray, FormControl, FormGroup, Validators } from '@angular/forms';
import { Subject, BehaviorSubject, takeUntil, map, startWith } from 'rxjs';
import { ToastrService } from 'ngx-toastr';
import { TranslateService } from '@ngx-translate/core';
import { UserService } from '../../../services/user.service';
import { EmployeeService } from '../../../services/employee.service';
import { CompanyService } from '../../../services/company.service';
import { AdditionalService } from '../../../services/additional.service';
import { EmployeeDeleteModalComponent } from '../employee-delete-modal/employee-delete-modal.component';
import { EmployeeData, EmployeeProject, LocationsList, TimeZones, CompanySettingsData } from '../../../interfaces';
import {
  EMPLOYEE_ROLES,
  EMPLOYEE_SYSTEM_STATUS,
  EMPLOYEE_AVAILABILITY,
  KIDS_GENDER,
  ACTION,
  SUBJECT, COMPANY_SETTINGS_STSTEM_ID
} from '../../../enums';
import { RULES } from '../../../constants';
import { PermissionService } from '../../../services/permission.service';
import { EmployeeFullNamePipe } from '../../../pipes/employee-full-name.pipe';

@Component({
  selector: 'app-employee-edit-modal',
  templateUrl: './employee-edit-modal.component.html',
  styleUrls: ['./employee-edit-modal.component.scss']
})
export class EmployeeEditModalComponent implements OnInit, OnDestroy {
  protected readonly ACTION = ACTION;
  protected readonly SUBJECT = SUBJECT;
  protected readonly EMPLOYEE_ROLES = EMPLOYEE_ROLES;
  protected readonly EMPLOYEE_SYSTEM_STATUS = EMPLOYEE_SYSTEM_STATUS;
  protected readonly EMPLOYEE_AVAILABILITY = EMPLOYEE_AVAILABILITY;
  protected readonly KIDS_GENDER = KIDS_GENDER;
  protected readonly RULES = RULES;

  private readonly destroy$ = new Subject<void>();

  data$ = new BehaviorSubject<EmployeeData | null>(null);

  protected readonly employeeRoles = Object.entries(EMPLOYEE_ROLES).map(([key, value]) => ({
    key,
    value
  }));

  isEditingOwnProfile: boolean = false;
  currentUserId: number | null = null;
  newProfileImageFile: File | null = null;
  profileImageRemoved = false;

  searchTitleQuery: string = '';
  searchPrimaryManagerQuery: string = '';
  searchSecondaryManagerQuery: string = '';
  searchLocationQuery: string = '';
  employeePrimaryManagers: EmployeeData[] = [];
  employeeSecondaryManagers: EmployeeData[] = [];
  filteredLocations: LocationsList[] = [];
  filteredEmployeePositionData: CompanySettingsData[] = [];

  timeZonesList$: BehaviorSubject<TimeZones[]> = new BehaviorSubject<TimeZones[]>([]);

  form: FormGroup = new FormGroup({
    titleIds: new FormControl([], [Validators.required]),
    firstName: new FormControl(null, [
      Validators.required,
      Validators.minLength(RULES.USER_NAME_MIN_LENGTH),
      Validators.maxLength(RULES.USER_NAME_MAX_LENGTH),
      Validators.pattern(RULES.USER_NAME_REGEX)
    ]),
    lastName: new FormControl(null, [
      Validators.minLength(RULES.USER_NAME_MIN_LENGTH),
      Validators.maxLength(RULES.USER_NAME_MAX_LENGTH),
      Validators.pattern(RULES.USER_NAME_REGEX)
    ]),
    startDate: new FormControl(new Date(), [Validators.required]),
    status: new FormControl(null),
    availability: new FormControl(null),
    birthDate: new FormControl(null),
    roles: new FormControl([]),
    email: new FormControl(null, [Validators.required, Validators.email]),
    personalEmail: new FormControl(null),
    phone: new FormControl(null, [
      Validators.minLength(RULES.PHONE_MIN_LENGTH),
      Validators.maxLength(RULES.PHONE_MAX_LENGTH),
      Validators.pattern(RULES.PHONE_REGEX)
    ]),
    contactAddress: new FormControl(null),
    leaveDate: new FormControl(null),
    leaveReason: new FormControl(null, [Validators.maxLength(RULES.SHORT_TEXT_MAX_LENGTH)]),
    primaryManager: new FormControl(null, [Validators.required]),
    secondaryManagers: new FormControl([]),
    needToKnow: new FormControl('', [Validators.maxLength(RULES.MIDDLE_TEXT_MAX_LENGTH)]),
    hrNote: new FormControl('', [Validators.maxLength(RULES.LONG_TEXT_MAX_LENGTH)]),
    projects: new FormArray([]),
    kids: new FormArray([]),
    fop: new FormControl(false),
    localTime: new FormControl(''),
    location: new FormControl(''),
    timeZone: new FormControl(''),
    linkedin: new FormControl(null, [Validators.pattern(RULES.URL_REGEX)]),
    instagram: new FormControl(null, [Validators.pattern(RULES.URL_REGEX)]),
    facebook: new FormControl(null, [Validators.pattern(RULES.URL_REGEX)]),
    telegram: new FormControl(null, [Validators.pattern(RULES.TELEGRAM_REGEX)])
  });

  get projects() {
    return this.form.get('projects') as FormArray;
  }

  get kids() {
    return this.form.get('kids') as FormArray;
  }

  constructor(
    public employeeService: EmployeeService,
    public companyService: CompanyService,
    private userService: UserService,
    protected permissionService: PermissionService,
    public additionalService: AdditionalService,
    private readonly toastrService: ToastrService,
    public dialogService: MatDialog,
    private readonly translate: TranslateService,
    public dialogRef: MatDialogRef<EmployeeEditModalComponent>,
    @Inject(MAT_DIALOG_DATA)
    public employee: EmployeeData | null,
    private fullNamePipe: EmployeeFullNamePipe
  ) {}

  ngOnInit(): void {
    this.currentUserId = this.userService.profileInfo$.getValue().id;

    if (this.employee) {
      if (this.currentUserId == this.employee.id) {
        this.isEditingOwnProfile = true;

        if (!this.companyService.employeesProjectData$.value) {
          this.companyService.updateEmployeeProjectList();
        }

        if (!this.companyService.employeesAvailabilityData$.value) {
          this.companyService.updateEmployeeAvailabilityList();
        }

        if (!this.companyService.employeesPositionData$.value) {
          this.companyService.updateEmployeePositionList();
        }

        if (!this.companyService.employeesStatusData$.value) {
          this.companyService.updateEmployeeStatusList();
        }
      }

      this.data$.next(this.employee);
      this.initFormData(this.employee);
    } else {
      this.data$.next({} as EmployeeData);
      this.form.get('roles')?.setValue([EMPLOYEE_ROLES.EMPLOYEE]);

      this.setDefaultAvailability();
      this.setDefaultStatus();
    }

    this.employeeService.managersList$.pipe(takeUntil(this.destroy$)).subscribe((employees) => {
      this.employeePrimaryManagers = [...employees];
      this.updateSecondaryManagerList();
    });

    this.form.get('primaryManager')?.valueChanges.subscribe(() => {
      this.updateSecondaryManagerList();
    });

    this.form.get('location')?.valueChanges.subscribe((value) => {
      this.initializeTimeZonesList(value, true);
    });

    this.filteredLocations = this.employeeService.locationsList$.getValue();

    this.companyService.employeesPositionData$.pipe(takeUntil(this.destroy$)).subscribe((items) => {
      this.filteredEmployeePositionData = items || [];
    });

    this.form.get('status')?.valueChanges.subscribe((status) => {
      const leaveDateControl = this.form.get('leaveDate');
      const leaveReasonControl = this.form.get('leaveReason');
      const availabilityControl = this.form.get('availability');

      if (status?.systemId == EMPLOYEE_SYSTEM_STATUS.RESIGNED || status?.systemId == EMPLOYEE_SYSTEM_STATUS.FIRED) {
        leaveDateControl?.setValidators([Validators.required]);
        leaveReasonControl?.setValidators([Validators.maxLength(RULES.SHORT_TEXT_MAX_LENGTH)]);
        availabilityControl?.setValue(null);
        availabilityControl?.clearValidators();
      } else {
        leaveDateControl?.setValue(null);
        leaveDateControl?.clearValidators();
        leaveReasonControl?.setValue(null);
        leaveReasonControl?.clearValidators();
      }

      leaveDateControl?.updateValueAndValidity();
      leaveReasonControl?.updateValueAndValidity();
      availabilityControl?.updateValueAndValidity();
    });

    this.dialogRef.afterClosed().subscribe(() => {
      this.employeeService.selectedId = null;
    });
  }

  initFormData(data: EmployeeData) {
    this.form.patchValue({
      titleIds: data.titles?.map((title: CompanySettingsData) => title.id) || [],
      firstName: data.firstName,
      lastName: data.lastName,
      startDate: data.startDate,
      status: this.additionalService.getCompanySettingsOptionValue(data.status),
      availability: this.additionalService.getCompanySettingsOptionValue(data.availability),
      birthDate: data.birthDate,
      roles: data.roles || [],
      email: data.email,
      personalEmail: data.personalEmail,
      phone: data.phone,
      contactAddress: data.contactAddress,
      leaveDate: data.leaveDate,
      leaveReason: data.leaveReason,
      primaryManager: data.primaryManager,
      secondaryManagers: data.secondaryManagers || [],
      needToKnow: data.needToKnow,
      hrNote: data.hrNote,
      fop: data.fop || false,
      localTime: data.localTime,
      location: data.location,
      timeZone: data.timeZone,
      linkedin: data.linkedin,
      instagram: data.instagram,
      facebook: data.facebook,
      telegram: data.telegram
    });

    if (data.status?.systemId === EMPLOYEE_SYSTEM_STATUS.RESIGNED || data.status?.systemId === EMPLOYEE_SYSTEM_STATUS.FIRED) {
      const availabilityControl = this.form.get('availability');
      availabilityControl?.clearValidators();
      availabilityControl?.updateValueAndValidity();
    }

    if (data.location) {
      this.initializeTimeZonesList(data.location);
    }

    this.initializeProjects(data.projects);
    this.initializeKids(data.kids);
    this.disableData();
  }

  private validateActiveEmployeeLimit(formData: any): boolean {
    if (!this.companyService.isEmployeesLimitExceed()) {
      return true;
    }

    const inactiveEmployeeStatuses: string[] = [EMPLOYEE_SYSTEM_STATUS.FIRED, EMPLOYEE_SYSTEM_STATUS.RESIGNED];
    const isEmployeeInactive = !this.employee?.status.systemId || inactiveEmployeeStatuses.includes(this.employee.status.systemId);
    const isStatusChangedToActive = !inactiveEmployeeStatuses.includes(formData.status.systemId);

    return !(isEmployeeInactive && isStatusChangedToActive);
  }

  initializeTimeZonesList(countryCode: string, updateValue: boolean = false): void {
    const locations = this.employeeService.locationsList$.getValue();
    const location = locations.find((loc) => loc.id === countryCode);

    const timeZones = location?.timeZones || [];
    this.timeZonesList$.next(timeZones);

    if (updateValue) {
      const firstTimeZone = timeZones.length ? timeZones[0].zoneId : '';
      this.form.get('timeZone')?.setValue(firstTimeZone);
    }
  }

  initializeProjects(projectsData: EmployeeProject[] = []) {
    projectsData.forEach((project) => {
      this.projects.push(
        new FormGroup({
          projectId: new FormControl(project.project.id, Validators.required),
          startDate: new FormControl(project.startDate, Validators.required),
          endDate: new FormControl(project.endDate)
        })
      );
    });
  }

  initializeKids(kidsData: any[] = []) {
    kidsData.forEach((kid) => {
      this.kids.push(
        new FormGroup({
          name: new FormControl(kid.name, Validators.required),
          gender: new FormControl(kid.gender, Validators.required),
          birthDate: new FormControl(kid.birthDate, Validators.required)
        })
      );
    });
  }

  setDefaultStatus() {
    this.companyService.employeesStatusData$.pipe(takeUntil(this.destroy$)).subscribe((items) => {
      if (!items) return;

      const status = items.find((item) => item.systemId === EMPLOYEE_SYSTEM_STATUS.ONBOARDING);

      const statusControl = this.form.get('status');
      if (status && !statusControl?.value) {
        statusControl?.setValue(this.additionalService.getCompanySettingsOptionValue(status));
      }
    });
  }


  setDefaultAvailability() {
    this.companyService.employeesAvailabilityData$.pipe(takeUntil(this.destroy$)).subscribe((items) => {
      if (!items) return;

      const availableItem = items.find((item) => item.systemId === EMPLOYEE_AVAILABILITY.AVAILABLE);

      const availabilityControl = this.form.get('availability');
      if (availableItem && !availabilityControl?.value) {
        availabilityControl?.setValue(this.additionalService.getCompanySettingsOptionValue(availableItem));
      }
    });
  }

  disableData() {
    if (!this.permissionService.roleAbility.can(ACTION.EDIT, SUBJECT.EMPLOYEE)) {
      for (let control in this.form.controls) {
        this.form.get(control)!.disable();
      }
    }

    if (this.isEditingOwnProfile) {
      const fieldsToEnable = [
        'personalEmail',
        'phone',
        'timeZone',
        'location',
        'contactAddress',
        'kids',
        'needToKnow',
        'linkedin',
        'instagram',
        'facebook',
        'telegram'
      ];

      for (const field of fieldsToEnable) {
        this.form.get(field)?.enable();
      }
    }

    if (this.employee) {
      this.form.get('email')?.disable();
    }
  }

  isFieldVisible(fieldName: string) {
    return !this.form.get(fieldName)?.disabled;
  }

  getSelectedRoleLabels(): EMPLOYEE_ROLES[] {
    const roles: string[] = this.form.get('roles')?.value || [];
    return roles.map((role) => this.translate.instant('employee.role.' + role + '.name'));
  }

  filterPrimaryManagers(event: any): void {
    this.searchPrimaryManagerQuery = event.target.value;
    this.employeePrimaryManagers = this.filterManagers(this.searchPrimaryManagerQuery);
  }

  filterSecondaryManagers(event: any): void {
    this.searchSecondaryManagerQuery = event.target.value;
    this.updateSecondaryManagerList();
  }

  updateSecondaryManagerList(): void {
    const primaryId = this.form.get('primaryManager')?.value;
    this.employeeSecondaryManagers = this.filterManagers(this.searchSecondaryManagerQuery, primaryId);
  }

  onSelectTitleOpen(isOpen: boolean): void {
    if (!isOpen) {
      this.searchTitleQuery = '';
      this.filteredEmployeePositionData = this.companyService.employeesPositionData$.getValue() || [];
    }
  }

  onSelectPrimaryManagerOpen(isOpen: boolean): void {
    if (!isOpen) {
      this.searchPrimaryManagerQuery = '';
      this.employeePrimaryManagers = this.filterManagers('');
    }
  }

  onSelectSecondaryManagerOpen(isOpen: boolean): void {
    if (!isOpen) {
      this.searchSecondaryManagerQuery = '';
      this.updateSecondaryManagerList();
    }
  }

  filterManagers(query: string, excludeId: number | null = null): EmployeeData[] {
    const allEmployees = this.employeeService.managersList$.getValue();

    return allEmployees
      .filter((employee) => this.fullNamePipe.transform(employee).toLowerCase().includes(query.toLowerCase()))
      .filter((employee) => employee.id !== excludeId);
  }

  filterLocations(event: any): void {
    this.searchLocationQuery = event.target.value.toLowerCase();

    this.filteredLocations = this.employeeService.locationsList$
      .getValue()
      .filter((location) => location.displayName.toLowerCase().includes(this.searchLocationQuery));
  }

  filterTitle(event: any): void {
    const target = event.target as HTMLInputElement;
    this.searchTitleQuery = target.value;
    const titles = this.companyService.employeesPositionData$.getValue();

    if (!titles) return;

    this.filteredEmployeePositionData = titles.filter((items) => items.name.toLowerCase().includes(this.searchTitleQuery.toLowerCase()));
  }

  onSelectLocationOpen(isOpen: boolean): void {
    if (!isOpen) {
      this.searchLocationQuery = '';
      this.filteredLocations = this.employeeService.locationsList$.getValue();
    }
  }

  onPhoneNumberInput(event: any) {
    const inputValue = event.target.value;
    const sanitizedValue = inputValue.replace(/[^+\d]/g, '');

    if (sanitizedValue !== inputValue) {
      this.form.get('phone')?.setValue(sanitizedValue, { emitEvent: false });
    }
  }

  addProjects() {
    this.projects.push(
      new FormGroup({
        projectId: new FormControl(null, Validators.required),
        startDate: new FormControl(null, Validators.required),
        endDate: new FormControl(null)
      })
    );
  }

  addKids() {
    this.kids.push(
      new FormGroup({
        name: new FormControl(null, Validators.required),
        gender: new FormControl(null, Validators.required),
        birthDate: new FormControl(null, Validators.required)
      })
    );
  }

  removeProject(index: number) {
    this.projects.removeAt(index);
  }

  removeKid(index: number) {
    this.kids.removeAt(index);
  }

  clearProjectDate(event: MouseEvent, fieldName: string, index: number) {
    event.stopPropagation();
    const control = this.projects.at(index).get(fieldName);
    if (control && !control.disabled) {
      control.setValue(null);
    }
  }

  onSubmit() {
    if (!this.form.valid) {
      this.additionalService.markFormGroupAsDirty(this.form);
      this.additionalService.scrollToFirstInvalidControl(this.form);
      return;
    }

    const translatedName = this.translate.instant('messages.instances.employee');
    const formData = this.additionalService.cleanFormData({ ...this.form.value });
    delete formData.localTime;

    if (!this.validateActiveEmployeeLimit(formData)) {
      const contactEmployee = this.companyService.companyInfo$.getValue()?.contactEmployee;
      this.toastrService.error(this.translate.instant('subscription.message.limit_exceed.employee', {billingOwner: contactEmployee?.email || ''}))
      return;
    }

    if (this.employee?.id) {
      if (this.permissionService.roleAbility.can(ACTION.EDIT, SUBJECT.EMPLOYEE) || this.isEditingOwnProfile) {
        this.employeeService.updateEmployeeRequest(this.employee.id, formData, this.newProfileImageFile || null, this.profileImageRemoved).subscribe({
          next: (response) => {
            if (response.success && response.data) {
              this.employeeService.updateEmployeesList();

              if (this.isEditingOwnProfile) {
                this.userService.getProfileInfoData();
              }
              this.dialogRef.close();

              this.toastrService.success(this.translate.instant('messages.updated_success', { name: translatedName }));
            } else {
              this.toastrService.error(this.translate.instant('messages.update_failed', { name: translatedName.toLowerCase() }));
            }
          }
        });
      }
    } else {
      this.employeeService.createEmployeeRequest(formData, this.newProfileImageFile || null).subscribe({
        next: (response) => {
          if (response.success && response.data) {
            this.dialogRef.close();
            this.employeeService.updateEmployeesList();
            this.toastrService.success(this.translate.instant('messages.created_success', { name: translatedName }));
          } else {
            this.toastrService.error(this.translate.instant('messages.create_failed', { name: translatedName.toLowerCase() }));
          }
        }
      });
    }
  }

  removeEmploeeModal(employee: EmployeeData) {
    const translatedName = this.translate.instant('messages.instances.employee');
    const companyName = this.companyService.companyInfo$.getValue()?.name;

    this.dialogService
      .open(EmployeeDeleteModalComponent, {
        panelClass: ['dialog-overlay-pane'],
        data: {
          employeeName: this.fullNamePipe.transform(employee),
          companyName: companyName
        },
        width: '500px'
      })
      .afterClosed()
      .subscribe((result: string) => {
        if (result === 'confirm') {
          this.employeeService.removeEmployeeRequest(this.employee!.id).subscribe({
            next: (response) => {
              if (response.success) {
                this.employeeService.updateEmployeesList();
                this.toastrService.success(this.translate.instant('messages.removed_success', { name: translatedName }));
                this.dialogService.closeAll();
              } else {
                this.toastrService.error(this.translate.instant('messages.remove_failed', { name: translatedName.toLowerCase() }));
              }
            }
          });
        }
      });
  }

  uploadPhoto(file: File) {
    this.profileImageRemoved = false;
    this.newProfileImageFile = file;
  }

  uploadError(message: string): void {
    this.toastrService.warning(message);
  }

  removePhoto() {
    this.profileImageRemoved = true;
    this.newProfileImageFile = null;
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
